import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getEmployee,
  updateEmployee,
  uploadEmployeeDocument,
  deleteEmployeeDocument,
  addEmploymentPeriod,
  deleteEmploymentPeriod,
  getCountries,
  getManagersLookup
} from '../utils/api';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowUpIcon,
  UserIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  KeyIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

const EmployeeDetail = () => {
  usePageTitle('Employee Details');

  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditEmployees, canDeleteEmployees } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [countries, setCountries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerSearch, setManagerSearch] = useState('');

  // Modal states
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);

  const [historyForm, setHistoryForm] = useState({
    start_date: '',
    end_date: '',
    employment_type: 'employment_contract',
    notes: ''
  });

  const [documentForm, setDocumentForm] = useState({
    document_name: '',
    document_type: 'contract',
    file: null
  });

  useEffect(() => {
    fetchEmployee();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Failed to load countries:', error);
      }
    };
    loadCountries();
  }, []);

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await getEmployee(id);
      setEmployee(response.data);
      setFormData({
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        company_email: response.data.company_email,
        personal_email: response.data.personal_email || '',
        phone: response.data.phone || '',
        date_of_birth: response.data.date_of_birth || '',
        home_address: response.data.home_address || '',
        country: response.data.country || '',
        position: response.data.position || '',
        manager_id: response.data.manager_id || '',
        employment_type: response.data.employment_type,
        current_start_date: response.data.current_start_date || '',
        current_end_date: response.data.current_end_date || '',
        is_active: response.data.is_active
      });
      // Set manager search text if there's a manager
      if (response.data.manager_name) {
        setManagerSearch(`${response.data.manager_name} (${response.data.manager_employee_id})`);
      } else {
        setManagerSearch('');
      }
    } catch (error) {
      toast.error('Failed to fetch employee');
      console.error(error);
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async (search) => {
    try {
      const response = await getManagersLookup({ exclude_id: id, search });
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Clean up formData - remove empty strings for optional fields
      const cleanData = { ...formData };
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '') {
          delete cleanData[key];
        }
      });

      await updateEmployee(id, cleanData);
      toast.success('Employee updated successfully');
      setEditMode(false);
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update employee');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddHistory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addEmploymentPeriod(id, historyForm);
      toast.success('Employment period added');
      setHistoryModalOpen(false);
      setHistoryForm({ start_date: '', end_date: '', employment_type: 'employment_contract', notes: '' });
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to add employment period');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHistory = async (historyId) => {
    if (!window.confirm('Delete this employment period?')) return;
    try {
      await deleteEmploymentPeriod(id, historyId);
      toast.success('Employment period deleted');
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to delete employment period');
      console.error(error);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!documentForm.file) {
      toast.error('Please select a file');
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('document', documentForm.file);
      formDataToSend.append('document_name', documentForm.document_name);
      formDataToSend.append('document_type', documentForm.document_type);

      await uploadEmployeeDocument(id, formDataToSend);
      toast.success('Document uploaded successfully');
      setDocumentModalOpen(false);
      setDocumentForm({ document_name: '', document_type: 'contract', file: null });
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to upload document');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteEmployeeDocument(id, docId);
      toast.success('Document deleted');
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to delete document');
      console.error(error);
    }
  };

  if (loading) return <div className="loading-overlay"><div className="spinner" /><span>Loading employee...</span></div>;
  if (!employee) return <div className="error">Employee not found</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/employees')}>
            <ArrowLeftIcon style={{ width: 16, height: 16 }} />
            Back to Employees
          </button>
          <h1 className="page-title" style={{ marginTop: '0.5rem' }}>
            {employee.first_name} {employee.last_name}
          </h1>
          <p className="page-subtitle">
            <span className="mono" style={{ color: 'var(--color-accent-info)' }}>{employee.employee_id}</span>
            {' • '}
            {employee.company_email}
          </p>
        </div>
        {canEditEmployees && !editMode && (
          <button className="btn btn-primary" onClick={() => setEditMode(true)}>
            <PencilIcon style={{ width: 18, height: 18 }} />
            Edit Employee
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          <UserIcon style={{ width: 18, height: 18 }} />
          Personal Info
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <BriefcaseIcon style={{ width: 18, height: 18 }} />
          Employment History
        </button>
        <button
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <DocumentTextIcon style={{ width: 18, height: 18 }} />
          Documents
        </button>
        <button
          className={`tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          <KeyIcon style={{ width: 18, height: 18 }} />
          User Account
        </button>
      </div>

      {/* Personal Info Tab */}
      {activeTab === 'personal' && (
        <div className="card">
          <form onSubmit={handleUpdate}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  className="form-input"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  className="form-input"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Company Email *</label>
                <input
                  type="email"
                  name="company_email"
                  className="form-input"
                  value={formData.company_email}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Personal Email</label>
                <input
                  type="email"
                  name="personal_email"
                  className="form-input"
                  value={formData.personal_email}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input
                  type="date"
                  name="date_of_birth"
                  className="form-input"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <select
                name="country"
                className="form-select"
                value={formData.country}
                onChange={handleInputChange}
                disabled={!editMode}
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Home Address</label>
              <textarea
                name="home_address"
                className="form-textarea"
                value={formData.home_address}
                onChange={handleInputChange}
                disabled={!editMode}
                rows="2"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Position</label>
                <input
                  type="text"
                  name="position"
                  className="form-input"
                  value={formData.position || ''}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  maxLength={100}
                  placeholder="e.g., Software Engineer"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Manager</label>
                {editMode ? (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search for manager..."
                      value={managerSearch}
                      onChange={(e) => {
                        setManagerSearch(e.target.value);
                        fetchManagers(e.target.value);
                      }}
                      onFocus={() => fetchManagers(managerSearch)}
                    />
                    {managers.length > 0 && (
                      <div className="dropdown-list" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        background: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        zIndex: 100
                      }}>
                        {managers.map((m) => (
                          <div
                            key={m.id}
                            className="dropdown-item"
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--color-border)'
                            }}
                            onClick={() => {
                              setFormData({ ...formData, manager_id: m.id });
                              setManagerSearch(`${m.first_name} ${m.last_name} (${m.employee_id})`);
                              setManagers([]);
                            }}
                          >
                            <strong>{m.first_name} {m.last_name}</strong>
                            <span style={{ color: 'var(--color-text-secondary)', marginLeft: '8px' }}>
                              {m.employee_id} {m.position && `• ${m.position}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(formData.manager_id || managerSearch) && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ marginTop: '8px', color: 'var(--color-accent-danger)' }}
                        onClick={() => {
                          setFormData({ ...formData, manager_id: null });
                          setManagerSearch('');
                          setManagers([]);
                        }}
                      >
                        Remove Manager
                      </button>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={employee?.manager_name ? `${employee.manager_name} (${employee.manager_employee_id})` : 'No manager assigned'}
                    disabled
                  />
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Employment Type</label>
                <select
                  name="employment_type"
                  className="form-select"
                  value={formData.employment_type}
                  onChange={handleInputChange}
                  disabled={!editMode}
                >
                  <option value="employment_contract">Employment Contract</option>
                  <option value="vendor_contract">Vendor Contract</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  name="current_start_date"
                  className="form-input"
                  value={formData.current_start_date}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  name="current_end_date"
                  className="form-input"
                  value={formData.current_end_date}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                  <span>Active Employee</span>
                </label>
              </div>
            </div>

            {editMode && (
              <div style={{
                marginTop: 'var(--space-xl)',
                display: 'flex',
                gap: 'var(--space-md)',
                justifyContent: 'flex-end',
                paddingTop: 'var(--space-lg)',
                borderTop: '1px solid var(--color-border-primary)'
              }}>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setEditMode(false);
                  fetchEmployee();
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>
      )}

      {/* Employment History Tab */}
      {activeTab === 'history' && (
        <div className="card">
          <div className="card-header">
            <h3>Employment History</h3>
            {canEditEmployees && (
              <button className="btn btn-primary btn-sm" onClick={() => setHistoryModalOpen(true)}>
                <PlusIcon style={{ width: 16, height: 16 }} />
                Add Period
              </button>
            )}
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Type</th>
                  <th>Notes</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employee.employment_history?.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-tertiary)' }}>
                      No employment history
                    </td>
                  </tr>
                ) : (
                  employee.employment_history?.map((period) => (
                    <tr key={period.id}>
                      <td>{new Date(period.start_date).toLocaleDateString()}</td>
                      <td>{period.end_date ? new Date(period.end_date).toLocaleDateString() : <span className="badge badge-paid">Present</span>}</td>
                      <td>
                        <span className={`badge ${period.employment_type === 'employment_contract' ? 'badge-approved' : 'badge-draft'}`}>
                          {period.employment_type === 'employment_contract' ? 'Employment' : 'Vendor'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--color-text-secondary)' }}>{period.notes || '—'}</td>
                      <td>
                        {canDeleteEmployees && (
                          <button
                            className="btn-icon btn-icon-danger"
                            onClick={() => handleDeleteHistory(period.id)}
                            title="Delete"
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="card">
          <div className="card-header">
            <h3>Documents</h3>
            {canEditEmployees && (
              <button className="btn btn-primary btn-sm" onClick={() => setDocumentModalOpen(true)}>
                <DocumentArrowUpIcon style={{ width: 16, height: 16 }} />
                Upload Document
              </button>
            )}
          </div>
          {employee.documents?.length === 0 ? (
            <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
              No documents uploaded
            </p>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Type</th>
                    <th>Uploaded</th>
                    <th>Uploaded By</th>
                    <th style={{ width: 150 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employee.documents?.map((doc) => (
                    <tr key={doc.id}>
                      <td><strong>{doc.document_name}</strong></td>
                      <td>
                        <span className={`badge badge-${doc.document_type === 'contract' ? 'sent' : 'draft'}`}>
                          {doc.document_type}
                        </span>
                      </td>
                      <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                      <td style={{ color: 'var(--color-text-secondary)' }}>{doc.uploaded_by_name || '—'}</td>
                      <td>
                        <div className="action-buttons">
                          {doc.file_url && (
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-icon"
                              title="View Document"
                            >
                              <DocumentTextIcon style={{ width: 16, height: 16 }} />
                            </a>
                          )}
                          {canDeleteEmployees && (
                            <button
                              className="btn-icon btn-icon-danger"
                              onClick={() => handleDeleteDocument(doc.id)}
                              title="Delete"
                            >
                              <TrashIcon style={{ width: 16, height: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* User Account Tab */}
      {activeTab === 'account' && (
        <div className="card">
          <h3 style={{ marginBottom: '1.5rem', color: 'var(--color-text-primary)' }}>Linked User Account</h3>
          {employee.user_account_id ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                    {employee.username}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                    {employee.user_email}
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <div style={{ padding: 'var(--space-sm)' }}>
                    <span className={`badge ${
                      employee.role === 'admin' ? 'badge-sent' :
                      employee.role === 'hr' ? 'badge-approved' :
                      employee.role === 'manager' ? 'badge-pending' : 'badge-draft'
                    }`}>
                      {employee.role}
                    </span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <div style={{ padding: 'var(--space-sm)' }}>
                    <span className={`badge ${employee.user_is_active ? 'badge-paid' : 'badge-unpaid'}`}>
                      {employee.user_is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--color-text-tertiary)' }}>No user account linked</p>
          )}
          <div className="info-box" style={{ marginTop: '1.5rem' }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              <strong>Note:</strong> User accounts are automatically created when an employee is added.
              Default password is <strong>"changeme123"</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Add History Modal */}
      {historyModalOpen && (
        <Modal title="Add Employment Period" onClose={() => setHistoryModalOpen(false)}>
          <form onSubmit={handleAddHistory}>
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={historyForm.start_date}
                onChange={(e) => setHistoryForm(prev => ({ ...prev, start_date: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date (leave empty if current)</label>
              <input
                type="date"
                value={historyForm.end_date}
                onChange={(e) => setHistoryForm(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Employment Type *</label>
              <select
                value={historyForm.employment_type}
                onChange={(e) => setHistoryForm(prev => ({ ...prev, employment_type: e.target.value }))}
                required
              >
                <option value="employment_contract">Employment Contract</option>
                <option value="vendor_contract">Vendor Contract</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={historyForm.notes}
                onChange={(e) => setHistoryForm(prev => ({ ...prev, notes: e.target.value }))}
                rows="3"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setHistoryModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Adding...' : 'Add Period'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Upload Document Modal */}
      {documentModalOpen && (
        <Modal title="Upload Document" onClose={() => setDocumentModalOpen(false)}>
          <form onSubmit={handleUploadDocument}>
            <div className="form-group">
              <label>Document Name *</label>
              <input
                type="text"
                value={documentForm.document_name}
                onChange={(e) => setDocumentForm(prev => ({ ...prev, document_name: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Document Type *</label>
              <select
                value={documentForm.document_type}
                onChange={(e) => setDocumentForm(prev => ({ ...prev, document_type: e.target.value }))}
                required
              >
                <option value="contract">Contract</option>
                <option value="id_document">ID Document</option>
                <option value="certificate">Certificate</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>File (PDF or Image) *</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDocumentForm(prev => ({ ...prev, file: e.target.files[0] }))}
                required
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDocumentModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default EmployeeDetail;
